import { z } from 'zod';

/**
 * Account modules (owner, 26 September 2026: "we can maybe turn on and off modules like auto qa and
 * dialer etc from the admin portal").
 *
 * A module is a whole area of the product an account is sold: off, and its menu, its pages and its
 * API are gone for that account, and nothing of it runs in the background. It is switched by the
 * provider (platform or reseller, module:write), never by the account itself.
 *
 * Within a module that is on, a SEAT still needs its own feature: the dialler dials only for seats
 * whose type (or override) includes `dialer`; Auto QA marks only calls on seats with `auto_qa`.
 * The module is the account's; the feature is the seat's.
 */

/**
 * `seat_feature` is the one a seat is charged per-seat for; `features` every seat feature the module
 * covers -- each counts for a seat only while its account has the module (0186, the database view
 * extension_enabled_features, seeded from the same list in module_features).
 */
export const MODULES = [
  {
    key: 'contact_centre',
    label: 'Contact centre',
    description: 'Queues, the agent workspace, wallboards and queue reports.',
    seat_feature: 'agent_controls',
    features: ['agent_controls', 'queue_member', 'agent_stats'],
  },
  {
    key: 'call_recording',
    label: 'Call recording',
    description: 'Calls recorded, stored and played back from call history.',
    seat_feature: 'call_recording',
    features: ['call_recording'],
  },
  {
    key: 'teams',
    label: 'Microsoft Teams',
    description: 'Teams Direct Routing: extensions ring and call out in Microsoft Teams.',
    seat_feature: 'teams',
    features: ['teams'],
  },
  {
    key: 'conversations',
    label: 'Conversations',
    description: 'WhatsApp, Telegram and SMS in the agent workspace.',
    seat_feature: null,
    features: [],
  },
  {
    key: 'dialler',
    label: 'Dialler',
    description: 'Lead lists, campaigns (preview and auto), call scripts, the do-not-call list and campaign reports.',
    seat_feature: 'dialer',
    features: ['dialer'],
  },
  {
    key: 'auto_qa',
    label: 'Auto QA',
    description: 'Calls transcribed and marked on the account’s own AI keys: scorecards, reports and search.',
    seat_feature: 'auto_qa',
    features: ['auto_qa'],
  },
  {
    key: 'click_to_dial',
    label: 'Click to dial',
    description: 'The Chrome extension and the click-to-dial API: a seat rings, then dials the number clicked.',
    seat_feature: 'click_to_dial',
    features: ['click_to_dial'],
  },
] as const;

/** The module a seat feature belongs to, if any. */
export function moduleOfFeature(feature: string): (typeof MODULES)[number] | null {
  return MODULES.find((m) => (m.features as readonly string[]).includes(feature)) ?? null;
}

export type ModuleKey = (typeof MODULES)[number]['key'];
export const MODULE_KEYS = MODULES.map((m) => m.key) as unknown as readonly [ModuleKey, ...ModuleKey[]];

export interface AccountModule {
  key: ModuleKey;
  label: string;
  description: string;
  enabled: boolean;
  /** Seats whose package (or an override) includes the module's seat feature. */
  seats: number;
  changed_at: string | null;
  changed_by: string | null;
  /**
   * What the account pays for it, monthly excluding VAT: ArcNet's catalogue price for a direct
   * client, the reseller's own price for a reseller's (null while the reseller has set none).
   */
  monthly_price: string | null;
  seat_price: string | null;
  currency: string;
  /** For a reseller's client: whether the reseller may sell it. Always true for a direct client. */
  available: boolean;
}

// ------------------------------------------------------------------ the catalogue (0186)
export interface CatalogueModule {
  key: ModuleKey;
  label: string;
  description: string;
  /** Monthly, excluding VAT, for the account having it. */
  monthly_price: string;
  /** Monthly, excluding VAT, for each extension using its seat feature; meaningless without one. */
  seat_price: string;
  currency: string;
  seat_feature: string | null;
  /** Accounts with it switched on, and seats using it, across the platform. */
  accounts: number;
  seats: number;
}

const Price = z.coerce.number().min(0).max(1_000_000);
export const CataloguePriceSchema = z.object({ monthly_price: Price, seat_price: Price }).strict();

/** What a reseller may sell, what ArcNet charges it, and its own price to its clients. */
export interface ResellerModule {
  key: ModuleKey;
  label: string;
  allowed: boolean;
  cost_monthly: string;
  cost_seat: string;
  price_monthly: string | null;
  price_seat: string | null;
  /** ArcNet's own catalogue price, for comparison. */
  list_monthly: string;
  list_seat: string;
  currency: string;
  seat_feature: string | null;
}
/** ArcNet's side: whether the reseller may sell it, and what it is charged. */
export const ResellerModuleTermsSchema = z.object({ allowed: z.boolean(), cost_monthly: Price, cost_seat: Price }).strict();
/** The reseller's side: its own price to its clients. */
export const ResellerModulePriceSchema = z.object({ price_monthly: Price, price_seat: Price }).strict();

export const ModuleUpdateSchema = z.object({ enabled: z.boolean() }).strict();
